import { defineStore } from 'pinia'

import * as service from '~/features-shared/favorites/api/favorites'
import type { IFavorite } from '~/features-shared/favorites/interfaces/favorite'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { logger } from '~/features-shared/libs/utils/logger'
import * as RTConv from '~/features-shared/rtconv/api/rtinterface-senders'

import { getUWPAppProperty } from '~/features-generic/uwp/api/uwp'

import type { SwymResponse } from '~/interfaces/services-dto'

type FavoriteSources = 'community' | 'dm' | 'ritual' | 'externaldm'

export const useFavoritesStore = defineStore('favorites', () => {
  const favorites = reactive<Map<FavoriteSources, Map<SUBJECT_6W_URI, number>>>(new Map())

  const get = (type: FavoriteSources) => favorites.get(type) || new Map() as Map<string, number>

  const fetch = async (type: FavoriteSources) => {
    const fav = get(type)
    const isCloud = await getUWPAppProperty('app.build.isCloud') === 'true'
    if (!isCloud || type !== 'externaldm') {
      // call to 3DComment for favorites should not be done for Cloud conversations
      // since it still contains old legacy favorites list 
      const result = (await service.getFavorites(type)).result
      result.forEach(el => fav.set(el.subject_uri, el.position))
    }
    if (!favorites.get(type))
      favorites.set(type, fav)

    return fav
  }

  const setAll = (type: FavoriteSources, favList: Map<string, number>) => {
    logger.log('fav setall', type, favList)
    if (type === 'externaldm') {
      const externalDMfaves = favorites.get(type)
      if (externalDMfaves) {
        logger.log('fav oldlist', externalDMfaves)
        externalDMfaves.clear()
        favList.forEach((value, key) => externalDMfaves.set(key, value))
        logger.log('fav newlist', externalDMfaves)
      }
      else {
        favorites.set(type, favList)
      }
    }
    else {
      throw new Error('setAll is only supported for external dms')
    }
  }

  const set = (type: FavoriteSources, subject_uri: SUBJECT_6W_URI, position?: number) => {
    const fav = get(type)
    if (position) { // add
      if (type !== 'externaldm') {
        fav.forEach((pos, id) => {
          if (pos >= position) {
            // to check with add etc
            // manage positions on UI only for non-externaldm favorites
            fav.set(id, pos + 1)
          }
        })
      }
      logger.log('fav set', subject_uri, position)
      fav.set(subject_uri, position)
      if (!favorites.get(type))
        favorites.set(type, fav)
    }
    else { // remove
      favorites.get(type)
        ?.delete(subject_uri)
    }
  }

  const remove = (type: FavoriteSources, subject_uri: string): Promise<SwymResponse<void>> => {
    favorites.get(type)
      ?.delete(subject_uri)
    if (type !== 'externaldm') { return service.removeFavorite(type, subject_uri) }
    else {
      return RTConv.setConversationFavorite({
        convId: Number(subject_uri.split(':')
          .pop() || subject_uri),
        favOrder: -1,
        waitForResponse: false,
      }) as Promise<string>
    }
  }

  const add = async (
    type: FavoriteSources,
    subject_uri: SUBJECT_6W_URI,
    position: number,
  ): Promise<SwymResponse<IFavorite>> => {
    set(
      type,
      subject_uri,
      position,
    )

    if (type !== 'externaldm') {
      const result = await service.addFavorite(
        type,
        subject_uri,
        position,
      )
      fetch(type)
      return result
    }
    else {
      const result = await RTConv.setConversationFavorite({
        convId: Number(subject_uri.split(':')
          .pop() || subject_uri),
        favOrder: position,
        waitForResponse: true,
      })
      return result
    }
  }

  return {
    favorites,
    get,
    set,
    setAll,
    add,
    fetch,
    remove,
  }
})
